import { useEffect } from 'react';
import { track, trackPageView } from '../utils/analytics.js';
import { buildCtaUrl, buildCalendlyUrl } from '../utils/attribution.js';
import { CALENDLY_URL } from '../utils/calendly.js';

const APP_STORE_BASE = 'https://apps.shopify.com/account-editor';

// Derive a utm_content label from an explicit data-cta attribute, else from the
// link text (slugified). Explicitly-wired CTAs already carry utm_content, so
// they never reach this path.
function labelFor(a) {
  const explicit = a.dataset?.cta;
  if (explicit) return explicit;
  const text = (a.textContent || '').trim().toLowerCase();
  const slug = text.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
  return slug || 'inline-link';
}

// The button part of an existing "<page>::<button>" utm_content, else a label
// derived from the link.
function ctaLabel(a) {
  try {
    const content = new URL(a.href).searchParams.get('utm_content') || '';
    const i = content.indexOf('::');
    if (i >= 0 && content.slice(i + 2)) return content.slice(i + 2);
  } catch {
    // fall through
  }
  return labelFor(a);
}

// SPA page views on route change + a single delegated listener that (1) is the
// attribution safety net for every App Store link and (2) fires the macro
// conversion event on click-through.
export default function useConversionTracking(pathname) {
  useEffect(() => {
    trackPageView(pathname);
  }, [pathname]);

  useEffect(() => {
    // Re-stamp App Store and Calendly links with the visitor's attribution at
    // click time. Render-time UTMs aren't enough: prerendered pages hydrate
    // with the hrefs baked in at build time (no visitor, so
    // accounteditor.com/referral), and React keeps those attributes as-is. The
    // button label after "::" in utm_content is kept; links without one (blog
    // in-content links) get a label from their text.
    const onActivate = (e) => {
      const cal = e.target.closest?.('a[href*="calendly.com/eric-accounteditor"]');
      if (cal) {
        try {
          cal.href = buildCalendlyUrl(CALENDLY_URL, { entryPoint: ctaLabel(cal) });
          track('demo_intent', {
            destination: 'calendly',
            placement: new URL(cal.href).searchParams.get('utm_content') || '',
          });
        } catch {
          // non-absolute href — ignore
        }
        return;
      }
      const a = e.target.closest?.('a[href*="apps.shopify.com/account-editor"]');
      if (!a) return;
      try {
        const current = new URL(a.href);
        // Keep the path, so a link to e.g. /account-editor/reviews stays there.
        const base = current.pathname.startsWith('/account-editor')
          ? `${current.origin}${current.pathname}`
          : APP_STORE_BASE;
        a.href = buildCtaUrl(base, ctaLabel(a));
        const finalUrl = new URL(a.href);
        track('install_intent', {
          destination: 'shopify_app_store',
          placement:
            finalUrl.searchParams.get('utm_content') ||
            finalUrl.searchParams.get('utm_medium') ||
            '',
        });
      } catch {
        // non-absolute href — ignore
      }
    };
    // click covers left-click + keyboard + cmd/ctrl-click; auxclick covers
    // middle-click (open in new tab).
    document.addEventListener('click', onActivate, { capture: true });
    document.addEventListener('auxclick', onActivate, { capture: true });
    return () => {
      document.removeEventListener('click', onActivate, { capture: true });
      document.removeEventListener('auxclick', onActivate, { capture: true });
    };
  }, []);
}
